import type { ColorMapping } from "@/cache/ColorMapCache";
import { ColorMapTexture } from "@/cache/ColorMapCache/ColorMapTexture";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import {
  type BinaryAnomalyDefinition,
  DEFAULT_BINARY_ANOMALY_DEFINITION,
  WIND_SPEED_ANOMALY_DEFINITION,
} from "./BinaryAnomalyDefinitions";

export const BINARY_ANOMALY_STYLE_ID = DEFAULT_BINARY_ANOMALY_DEFINITION.style.id;
export const WIND_SPEED_ANOMALY_STYLE_ID = WIND_SPEED_ANOMALY_DEFINITION.style.id;
export const BINARY_ANOMALY_MIN_VALUE = DEFAULT_BINARY_ANOMALY_DEFINITION.style.min;
export const BINARY_ANOMALY_MAX_VALUE = DEFAULT_BINARY_ANOMALY_DEFINITION.style.max;
export const BINARY_ANOMALY_COLOR_STEP = DEFAULT_BINARY_ANOMALY_DEFINITION.style.step;
export const BINARY_ANOMALY_COLOR_SAMPLE_COUNT =
  (BINARY_ANOMALY_MAX_VALUE - BINARY_ANOMALY_MIN_VALUE) / BINARY_ANOMALY_COLOR_STEP + 1;

const DIVERGING_ANCHORS = new Uint8ClampedArray([
  49, 54, 149, 255, 69, 117, 180, 255, 116, 173, 209, 255, 224, 243, 248, 255, 255, 255, 255, 255, 254, 224, 144, 255,
  244, 109, 67, 255, 215, 48, 39, 255, 103, 0, 31, 255,
]);

function interpolateChannel(left: number, right: number, fraction: number): number {
  return Math.round(left + (right - left) * fraction);
}

export function createBinaryAnomalyColorSamples(): Uint8ClampedArray {
  const samples = new Uint8ClampedArray(BINARY_ANOMALY_COLOR_SAMPLE_COUNT * 4);
  const anchorCount = DIVERGING_ANCHORS.length / 4;

  for (let sampleIndex = 0; sampleIndex < BINARY_ANOMALY_COLOR_SAMPLE_COUNT; sampleIndex++) {
    const anchorPosition = (sampleIndex / (BINARY_ANOMALY_COLOR_SAMPLE_COUNT - 1)) * (anchorCount - 1);
    const leftAnchor = Math.floor(anchorPosition);
    const rightAnchor = Math.min(leftAnchor + 1, anchorCount - 1);
    const fraction = anchorPosition - leftAnchor;
    for (let channel = 0; channel < 4; channel++) {
      samples[sampleIndex * 4 + channel] = interpolateChannel(
        DIVERGING_ANCHORS[leftAnchor * 4 + channel],
        DIVERGING_ANCHORS[rightAnchor * 4 + channel],
        fraction,
      );
    }
  }

  return samples;
}

function sampleToHex(samples: Uint8ClampedArray, sampleIndex: number): string {
  return `#${[0, 1, 2].map((channel) => samples[sampleIndex * 4 + channel].toString(16).padStart(2, "0")).join("")}`;
}

export function createBinaryAnomalyLegendColormap(
  definition: BinaryAnomalyDefinition = DEFAULT_BINARY_ANOMALY_DEFINITION,
): ColormapCreate {
  const samples = createBinaryAnomalyColorSamples();
  return {
    type: "segmented",
    label: definition.legendLabel,
    stops: Array.from({ length: BINARY_ANOMALY_COLOR_SAMPLE_COUNT }, (_, sampleIndex) => [
      definition.style.min + sampleIndex * definition.style.step,
      sampleToHex(samples, sampleIndex),
    ]),
  };
}

export const BinaryAnomalyLegendColormap = createBinaryAnomalyLegendColormap();
export const WindSpeedAnomalyLegendColormap = createBinaryAnomalyLegendColormap(WIND_SPEED_ANOMALY_DEFINITION);

export function createBinaryAnomalyColorMapping(
  definition: BinaryAnomalyDefinition = DEFAULT_BINARY_ANOMALY_DEFINITION,
): ColorMapping {
  return {
    texture: new ColorMapTexture(createBinaryAnomalyColorSamples(), true),
    recommendedValueRange: { lowerBound: definition.style.min, upperBound: definition.style.max },
    colormap: definition.style.id,
  };
}
